import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { deleteBoardMember, fetchBoardMembers } from '../boardApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { useBranchFilter } from '../branchFilter';
import './BranchesPage.css';
import './BoardPage.css';

const PAGE_SIZE = 10;

const MEMBERSHIP_TYPE_KEYS = {
  'رئيس مجلس الإدارة': 'directors.typeChairman',
  'نائب رئيس مجلس الإدارة': 'directors.typeVice',
  'عضو تنفيذي': 'directors.typeExecutive',
  'عضو غير تنفيذي': 'directors.typeNonExecutive',
  'عضو مستقل': 'directors.typeIndependent',
};

export default function DirectorsPage() {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { selectedBranch: globalBranch } = useBranchFilter();

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const data = await fetchBoardMembers();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('directors.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const membershipLabel = (v) => {
    const key = MEMBERSHIP_TYPE_KEYS[v];
    return key ? t(key) : (v || '—');
  };

  const partnerLabel = (v) => {
    if (v === 'شريك') return t('directors.partner');
    if (v === 'مساهم') return t('directors.shareholder');
    return v || '—';
  };

  const types = useMemo(() => Array.from(new Set(rows.map((x) => x.membershipType).filter(Boolean))), [rows]);

  const filtered = useMemo(() => rows.filter((x) => {
    const q = search.trim().toLowerCase();
    const qOk = !q
      || (x.name || '').toLowerCase().includes(q)
      || (x.nationalId || '').toLowerCase().includes(q)
      || (x.nationality || '').toLowerCase().includes(q);
    const tOk = typeFilter === 'all' || x.membershipType === typeFilter;
    const bOk = globalBranch === 'all' || x.branch === globalBranch;
    return qOk && tOk && bOk;
  }), [rows, search, typeFilter, globalBranch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeCount = rows.filter((x) => x.status === 'active').length;

  const fmt = (d) => (d ? String(d).slice(0, 10) : '—');

  const openDelete = (row) => { setDeleteTarget(row); setConfirmOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBoardMember(deleteTarget.id);
      setRows((prev) => prev.filter((x) => x.id !== deleteTarget.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('directors.deleteFailed'));
    } finally {
      setConfirmOpen(false); setDeleteTarget(null);
    }
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }} dir={direction}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="board" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('directors.title')}</span>
            <span className="list-page-card__sub">{t('directors.subtitle')} — {t('directors.votesNote')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={() => navigate(`${basePath}/board/new`)}><Icon name="plus" size={14} color="#fff" /> {t('directors.add')}</button>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('directors.loading')}</div>}

      {!loading && (
        <>
          <div className="page__stats">
            <div className="page__stat-chip page__stat-chip--total"><Icon name="user" size={13} /> {t('common.total')}: {rows.length}</div>
            <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t('common.active')}: {activeCount}</div>
            <div className="page__stat-chip page__stat-chip--new"><Icon name="x-circle" size={13} /> {t('common.inactive')}: {rows.length - activeCount}</div>
          </div>

          <div className="page__filters">
            <div className="page__search">
              <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
              <input className="page__search-input" placeholder={t('directors.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="page__filter-select" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="all">{t('directors.allTypes')}</option>
              {types.map((ty) => <option key={ty} value={ty}>{membershipLabel(ty)}</option>)}
            </select>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>{t('directors.colMember')}</th>
                  <th>{t('directors.colNationality')}</th>
                  <th>{t('directors.colNationalId')}</th>
                  <th>{t('directors.colMembershipType')}</th>
                  <th>{t('directors.colMembership')}</th>
                  <th>{t('directors.colPartnerKind')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><div className="empty-state__title">{t('directors.noMatch')}</div></div></td></tr>
                ) : paginated.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="td-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td>
                      <div className="branch-name-cell">
                        {item.photoData
                          ? <img src={item.photoData} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1px solid #eceff1' }} />
                          : <div className="board-avatar">{(item.name || '؟').charAt(0)}</div>}
                        <div>
                          <strong style={{ color: '#00334a', fontSize: '0.88rem', display: 'block' }}>{item.name}</strong>
                          {item.birthDate && <span style={{ fontSize: '0.74rem', color: '#90a4ae' }} dir="ltr">{fmt(item.birthDate)}</span>}
                        </div>
                      </div>
                    </td>
                    <td>{item.nationality || '—'}</td>
                    <td dir="ltr" style={{ textAlign: 'inherit' }}>{item.nationalId || '—'}</td>
                    <td><span className="position-badge">{membershipLabel(item.membershipType)}</span></td>
                    <td dir="ltr" style={{ textAlign: 'inherit', fontSize: '0.82rem', color: '#546e7a' }}>
                      {item.membershipStartDate || item.membershipEndDate
                        ? <>{fmt(item.membershipStartDate)} — {fmt(item.membershipEndDate)}</>
                        : '—'}
                    </td>
                    <td>
                      {item.partnerKind
                        ? <span className={`users-status users-status--${item.partnerKind === 'شريك' ? 'active' : 'inactive'}`}>{partnerLabel(item.partnerKind)}</span>
                        : '—'}
                    </td>
                    <td>
                      <div className="td-actions">
                        <button type="button" className="action-btn action-btn--edit" title={t('common.edit')} onClick={() => navigate(`${basePath}/board/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                        <button type="button" className="action-btn action-btn--delete" title={t('common.delete')} onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > PAGE_SIZE && (
              <div className="pagination">
                <span>{t('common.showing')} {Math.min(filtered.length, (page - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, page * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
                <div className="pagination__pages">
                  <button type="button" className="pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => <button type="button" key={p} className={`pagination__btn${page === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
                  <button type="button" className="pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.name}
        title={t('common.deleteConfirmTitle')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
}
